import { useCallback, useEffect, useMemo } from "react";

import {
    formatNum,
    Result,
    roundToDecimals,
    formatPercent,
    calculatePercentChange,
    lamportsToUiAmount,
    decimalsToBps
} from "@bridgesplit/utils";
import { RaydiumPositionExpanded, TokenPositionExpanded } from "@bridgesplit/abf-sdk";
import { SqrtPriceMath, TickMath } from "@raydium-io/raydium-sdk-v2";
import {
    calculateHealthRatioFromLtv,
    calculateLoanHealth,
    getHealthStatus,
    getZcLedger,
    RAYDIUM_MAX_TICK_INDEX,
    RAYDIUM_MIN_TICK_INDEX,
    useGetLoanLutQuery
} from "@bridgesplit/abf-react";
import Decimal from "decimal.js";
import BN from "bn.js";
import { Slippage, HealthDetails } from "app/components/common";

import { AUTO_SLIPPAGE_DECIMALS, useRaydiumContext } from "./RaydiumContext";
import { getPoolData, getPositionData } from "./raydium";
import { PRICE_RANGE_DECIMALS } from "../whirlpool/common";
import { useClmmRefinanceInfo } from "../clmm/util";
import { ActionType } from "../clmm/ManagePositionWrapper";

export function useInitLuts() {
    const { loanExpanded, isActionTypeInitialized, setIsActionTypeInitialized, setRefetchLuts } = useRaydiumContext();
    const {
        data: lutsDepositAndWithdraw,
        isFetching: isFetchingDepositAndWithdraw,
        refetch: refetchDepositWithdraw
    } = useGetLoanLutQuery({
        loan: loanExpanded.loan.address,
        actionType: 0
    });
    const {
        data: lutsOpen,
        isFetching: isFetchingOpen,
        refetch: refetchOpen
    } = useGetLoanLutQuery({
        loan: loanExpanded.loan.address,
        actionType: 1
    });

    const isFetching = isFetchingDepositAndWithdraw || isFetchingOpen;

    const refetchLutsFunction = useCallback(async () => {
        await Promise.all([refetchDepositWithdraw(), refetchOpen()]);
    }, [refetchDepositWithdraw, refetchOpen]);

    useEffect(() => {
        setRefetchLuts(() => refetchLutsFunction);
    }, [refetchLutsFunction, setRefetchLuts]);

    useEffect(() => {
        if (isFetching) {
            return;
        }

        const initializedActionTypes: ActionType[] = [];
        if (lutsDepositAndWithdraw != null) {
            initializedActionTypes.push("deposit");
            initializedActionTypes.push("withdraw");
        }

        if (lutsOpen != null) initializedActionTypes.push("open");

        // Check if the arrays are different before updating
        const shouldUpdate =
            isActionTypeInitialized === undefined ||
            isActionTypeInitialized.length !== initializedActionTypes.length ||
            initializedActionTypes.some((type) => !isActionTypeInitialized.includes(type));

        if (shouldUpdate) {
            setIsActionTypeInitialized(initializedActionTypes);
        }
    }, [lutsDepositAndWithdraw, lutsOpen, isActionTypeInitialized, isFetching, setIsActionTypeInitialized]);
}

export function useInitPositionData() {
    const { positionData, setPositionData, raydiumPosition, setInitialWithdrawQuote, loanExpanded } =
        useRaydiumContext();

    useEffect(() => {
        if (positionData !== undefined) return;
        (async () => {
            const data = await getPositionData(raydiumPosition, loanExpanded.loan.address);
            setPositionData(data?.positionData ?? null);
            setInitialWithdrawQuote(data?.quote);
        })();
    }, [loanExpanded.loan.address, positionData, raydiumPosition, setInitialWithdrawQuote, setPositionData]);
}

export function useInitPoolData() {
    const { poolData, setPoolData, raydiumPosition, loanExpanded } = useRaydiumContext();

    useEffect(() => {
        if (poolData !== undefined) return;
        (async () => {
            const data = await getPoolData(raydiumPosition.poolAddress, loanExpanded.loan.address);
            setPoolData(data ?? null);
        })();
    }, [poolData, setPoolData, raydiumPosition, loanExpanded.loan.address]);
}

export function useRaydiumFormatPrice() {
    const { isFlippedPrice, raydiumPosition } = useRaydiumContext();
    return useCallback(
        (tickIndex: number | undefined, decimals = PRICE_RANGE_DECIMALS) => {
            if (tickIndex === undefined) return undefined;

            const price = getPriceFromTick(tickIndex, raydiumPosition);
            const flippedPrice = flipPriceIfNeeded(price, isFlippedPrice);
            return formatNum(flippedPrice, { customDecimals: decimals });
        },
        [isFlippedPrice, raydiumPosition]
    );
}

export function useFormatRaydiumPercentChangeFromCurrent() {
    const { raydiumPosition, isFlippedPrice } = useRaydiumContext();
    return useCallback(
        (tickIndex: number | undefined, currentTickIndex: number | undefined) => {
            if (tickIndex === undefined || currentTickIndex === undefined) return undefined;

            const price = getPriceFromTick(tickIndex, raydiumPosition);
            const currentPrice = getPriceFromTick(currentTickIndex, raydiumPosition);

            let percentChange = calculatePercentChange(currentPrice, price);
            if (isFlippedPrice) {
                percentChange = -percentChange;
            }
            let formatted = formatPercent(percentChange);
            if (percentChange > 0) formatted = `+${formatted}`;
            return formatted;
        },
        [isFlippedPrice, raydiumPosition]
    );
}

//Move to CLMM utils
export function flipPriceIfNeeded(price: number | undefined, isFlippedPrice: boolean) {
    if (!price) return price;
    return isFlippedPrice ? roundToDecimals(1 / price, PRICE_RANGE_DECIMALS) : price;
}

export function isMaxTickIndex(tickIndex: number, tickSpacing: number) {
    const maxTickIndex = getNearestValidTickIndex(RAYDIUM_MAX_TICK_INDEX, tickSpacing);
    return tickIndex === maxTickIndex;
}

export function isMinTickIndex(tickIndex: number, tickSpacing: number) {
    const minTickIndex = getNearestValidTickIndex(RAYDIUM_MIN_TICK_INDEX, tickSpacing);
    return tickIndex === minTickIndex;
}

export function getPriceFromTick(tickIndex: number, position: RaydiumPositionExpanded) {
    try {
        if (isMaxTickIndex(tickIndex, position.pool.tickSpacing)) return Infinity;
        if (isMinTickIndex(tickIndex, position.pool.tickSpacing)) return 0;

        const roundedPrice = TickMath.roundPriceWithTickspacing(
            SqrtPriceMath.sqrtPriceX64ToPrice(
                SqrtPriceMath.getSqrtPriceX64FromTick(tickIndex),
                position.tokenA.decimals,
                position.tokenB.decimals
            ),
            position.pool.tickSpacing,
            position.tokenA.decimals,
            position.tokenB.decimals
        );

        return roundedPrice.toNumber();
    } catch (error) {
        Result.err(error);
        return 0;
    }
}
export function getTickFromPrice(price: number, tickSpacing: number, raydiumPosition: RaydiumPositionExpanded) {
    try {
        if (price === Infinity) return RAYDIUM_MAX_TICK_INDEX;
        if (price === 0) return RAYDIUM_MIN_TICK_INDEX;

        return TickMath.getTickWithPriceAndTickspacing(
            new Decimal(price),
            tickSpacing,
            raydiumPosition.tokenA.decimals,
            raydiumPosition.tokenB.decimals
        );
    } catch (error) {
        Result.err(error);
        return 0;
    }
}
function getNearestValidTickIndex(tickIndex: number, tickSpacing: number): number {
    // Round tickIndex to nearest multiple of tickSpacing
    return Math.round(tickIndex / tickSpacing) * tickSpacing;
}

export function useInitialRaydiumRatio() {
    const { raydiumPosition } = useRaydiumContext();
    return useMemo(() => {
        if (!raydiumPosition) return undefined;

        try {
            const { tickLowerIndex, tickUpperIndex } = raydiumPosition.position;
            const sqrtPriceX64 = raydiumPosition.pool.sqrtPriceX64;
            const tokenADecimals = raydiumPosition.tokenA.decimals;
            const tokenBDecimals = raydiumPosition.tokenB.decimals;

            return calculateRaydiumDepositRatios(
                sqrtPriceX64,
                tokenADecimals,
                tokenBDecimals,
                tickLowerIndex,
                tickUpperIndex
            );
        } catch (error) {
            return undefined;
        }
    }, [raydiumPosition]);
}

export function calculateRaydiumDepositRatios(
    sqrtPriceX64: BN,
    tokenADecimals: number,
    tokenBDecimals: number,
    tickLowerIndex: number,
    tickUpperIndex: number
) {
    // Get price as a decimal
    const price = SqrtPriceMath.sqrtPriceX64ToPrice(sqrtPriceX64, tokenADecimals, tokenBDecimals).toNumber();

    // Calculate lower and upper sqrt prices
    const lowerSqrtPrice = Math.sqrt(
        SqrtPriceMath.sqrtPriceX64ToPrice(
            SqrtPriceMath.getSqrtPriceX64FromTick(tickLowerIndex),
            tokenADecimals,
            tokenBDecimals
        ).toNumber()
    );
    const upperSqrtPrice = Math.sqrt(
        SqrtPriceMath.sqrtPriceX64ToPrice(
            SqrtPriceMath.getSqrtPriceX64FromTick(tickUpperIndex),
            tokenADecimals,
            tokenBDecimals
        ).toNumber()
    );
    const clampedCurrentSqrtPrice = Math.min(Math.max(lowerSqrtPrice, Math.sqrt(price)), upperSqrtPrice);

    // Calculate deposit A and B
    const depositA = 1 / clampedCurrentSqrtPrice - 1 / upperSqrtPrice;
    const depositB = clampedCurrentSqrtPrice - lowerSqrtPrice;

    const depositAValueInB = depositA * price;
    const depositBValueInB = depositB;
    const totalValueInB = depositAValueInB + depositBValueInB;

    const ratioA = depositAValueInB / totalValueInB;
    const ratioB = depositBValueInB / totalValueInB;

    return { ratioA, ratioB };
}

export function useRaydiumLtv(positionUsdValue: number | undefined) {
    const {
        loanExpanded,
        slippageController: { slippagePercentDecimals }
    } = useRaydiumContext();

    const firstLedger = getZcLedger(loanExpanded);

    const { refinanceInfo } = useClmmRefinanceInfo(loanExpanded);

    const totalDebtUsd = (loanExpanded?.principalUsdPrice ?? 0) * (firstLedger?.ledgerDebt.total ?? 0);
    const ltv = positionUsdValue !== undefined ? totalDebtUsd / positionUsdValue : 1;

    const maxLtvWithSlippage = refinanceInfo?.ltv
        ? refinanceInfo.ltv * (1 + (slippagePercentDecimals ?? AUTO_SLIPPAGE_DECIMALS))
        : 1;
    const insufficientCollateral = refinanceInfo && ltv > maxLtvWithSlippage;

    const { health, loanHealthStatus, ltv: loanLtv } = calculateLoanHealth(loanExpanded);

    const originalHealth: HealthDetails = {
        health,
        loanHealthStatus,
        ltv: loanLtv
    };

    const newHealth: HealthDetails = {
        health: calculateHealthRatioFromLtv(ltv, refinanceInfo?.liquidationThreshold),
        loanHealthStatus: getHealthStatus(refinanceInfo?.ltv, refinanceInfo?.ltv, refinanceInfo?.liquidationThreshold),
        ltv
    };

    return {
        ltv,
        refinanceInfo,
        insufficientCollateral,
        originalHealth,
        isLoading: positionUsdValue === undefined,
        newHealth
    };
}

export function getTokenUsdPrice(token: TokenPositionExpanded, amount: BN) {
    return lamportsToUiAmount(amount.toNumber(), token.decimals) * token.usdPrice;
}

// No support for auto slippage on raydium yet, so we use a default of 0.1%
export function getSafeSlippageDecimals(slippageController: ReturnType<typeof Slippage.useController>) {
    if (slippageController.isAutoSlippage || !slippageController.slippagePercentDecimals) {
        return decimalsToBps(0.1, "bps");
    }
    return slippageController.slippagePercentDecimals;
}
